import { captureException } from '@sentry/nextjs';
import { getStore } from '@/reducers/index';
import { mapSearchStateToSearchParams } from '@/reducers/items';
import { updateSearchId } from '@/reducers/search';
import { mapSearchToPublicURL } from '@/tools/search';
import {
  loadedFromHistoryNavigation,
  loadedFromReload,
} from '@/shared/helpers/getNavigationType';
import {
  isComingFromHomepage,
  isComingFromListing,
} from '../utilities/referrer-check';
import {
  itemsKey,
  listingUrlKey,
  searchIdKey,
  searchParametersKey,
} from './constants';
import { SearchNavigation } from './navigation-data';

const shouldGenerateNewSearchId = (): boolean => {
  // In both the reload and history navigation cases, the referrer is useless.
  // We maintain the ID in both cases anyway.
  if (loadedFromReload() || loadedFromHistoryNavigation()) {
    return false;
  } else {
    return (
      isComingFromHomepage(window.document.referrer) ||
      isComingFromListing(window.document.referrer) ||
      window.document.referrer == ''
    );
  }
};

const getOrGenerateSearchId = (): string => {
  const store = getStore(undefined, false);

  if (shouldGenerateNewSearchId()) {
    const id = crypto.randomUUID();
    store.dispatch(updateSearchId(id));
    return id;
  }

  const sessionId = window.sessionStorage.getItem(searchIdKey);
  if (sessionId) {
    store.dispatch(updateSearchId(sessionId));
    return sessionId;
  } else {
    const id = crypto.randomUUID();
    store.dispatch(updateSearchId(id));
    return id;
  }
};

const writeItemNavigationDataToStorage = (): void => {
  const store = getStore(undefined, false);

  try {
    const {
      search: searchState,
      items: itemState,
      filtersConfig: filtersConfigState,
    } = store.getState();
    const searchParams = mapSearchStateToSearchParams(
      searchState,
      filtersConfigState
    );

    const items = itemState.list.map(({ item }) => ({
      urn: item.urn,
      url: item.urls.default,
    }));

    const pageData: SearchNavigation = {
      from: Number(searchParams.start) || 0,
      items,
      fetchedToTheEnd: items.length < (Number(searchParams.lim) || 30),
    };

    /*
      The data is written to sessionStorage to speed the access
      from the detail page, and written to localStorage so that
      opening an ad in a new tab still allows navigation of the search.
      The localStorage is of course updated globally, so each ad detail
      makes a copy into its own sessionStorage immediately when fetching
      the navigation data.
    */

    const itemsData = JSON.stringify(pageData);

    window.sessionStorage.setItem(itemsKey, itemsData);
    window.localStorage.setItem(itemsKey, itemsData);

    const searchParametersData = JSON.stringify(searchParams);

    window.sessionStorage.setItem(searchParametersKey, searchParametersData);
    window.localStorage.setItem(searchParametersKey, searchParametersData);

    const listingUrlData =
      location.origin +
      mapSearchToPublicURL(searchState, filtersConfigState).url;

    window.sessionStorage.setItem(listingUrlKey, listingUrlData);
    window.localStorage.setItem(listingUrlKey, listingUrlData);

    const searchId = searchState.id || getOrGenerateSearchId();

    window.sessionStorage.setItem(searchIdKey, searchId);
    window.localStorage.setItem(searchIdKey, searchId);
  } catch (e) {
    console.groupCollapsed('Item Navigation on Listing');
    console.error('There was a problem while storing the navigation data.');
    console.error(e);
    console.groupEnd();
    captureException(e, { level: 'warning' });
  }
};

export default writeItemNavigationDataToStorage;
